import { v4 as uuidv4 } from 'uuid';
import { normalizeMaintenanceWindow, timeDiffMaintenance } from './date';

const MAINTENANCE_CHECK_TIMEOUT = 5000;

const withMaintenanceTimeout = (promise) => new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
        reject(new Error('Maintenance check timed out'));
    }, MAINTENANCE_CHECK_TIMEOUT);

    promise
        .then((value) => {
            clearTimeout(timer);
            resolve(value);
        })
        .catch((error) => {
            clearTimeout(timer);
            reject(error);
        });
});

const normalizeLocalizedContent = (content) => {
    if (typeof content === 'string') {
        return content;
    }

    if (content && typeof content === 'object') {
        return content;
    }

    return undefined;
};

const mergeMaintenanceContent = (apiContent, fallbackContent) => {
    if (!apiContent) {
        return fallbackContent;
    }

    if (
        apiContent &&
        fallbackContent &&
        typeof apiContent === 'object' &&
        typeof fallbackContent === 'object'
    ) {
        return {
            ...fallbackContent,
            ...apiContent,
        };
    }

    return apiContent;
};

const normalizeMaintenanceApiPayload = (payload = {}) => {
    const data = payload?.data || payload;

    if (typeof data?.maintenanceWindowFlag !== 'boolean') {
        return null;
    }

    return normalizeMaintenanceWindow({
        status: data.maintenanceWindowFlag,
        startDate: data.startDate || data.startTime,
        endDate: data.endDate || data.endTime,
        title: normalizeLocalizedContent(
            data.maintenanceWindowTitle || data.title || data.maintenanceTitle
        ),
        description: normalizeLocalizedContent(
            data.maintenanceWindowDescription || data.description || data.maintenanceDescription || data.message
        ),
    });
};

export const isMaintenancePageRequired = (maintenanceData = {}) => {
    const normalizedMaintenanceWindow = normalizeMaintenanceWindow(maintenanceData);

    if (!normalizedMaintenanceWindow.status) {
        return false;
    }

    return timeDiffMaintenance(
        normalizedMaintenanceWindow.startDate,
        normalizedMaintenanceWindow.endDate
    ).status;
};

export const getPreloadedMaintenanceContext = async () => {
    const maintenanceContextPromise = globalThis.maintenanceContextPromise;

    if (maintenanceContextPromise && typeof maintenanceContextPromise.then === 'function') {
        try {
            const resolvedMaintenanceContext = await maintenanceContextPromise;
            return normalizeMaintenanceWindow(globalThis.maintenanceContext || resolvedMaintenanceContext || {});
        } catch (error) {
            return normalizeMaintenanceWindow(globalThis.maintenanceContext || {});
        }
    }

    return normalizeMaintenanceWindow(globalThis.maintenanceContext || {});
};

export const fetchMaintenanceLocalConfig = async () => {
    const response = await fetch('./config/maintenance.json', {
        method: 'GET',
        mode: 'cors',
    });

    if (typeof response?.json === 'function') {
        return await response.json() || {};
    }

    if (typeof response?.body === 'string') {
        try {
            return JSON.parse(response.body) || {};
        } catch (error) {
            return {};
        }
    }

    return response?.body || {};
};

export const fetchMaintenanceApiStatus = async () => {
    const maintenanceStatusApi = process.env.maintenanceStatusApi;
    const maintenanceJourneyName = process.env.maintenanceJourneyName;

    console.log('[maintenance] fetch config', {
        maintenanceStatusApi,
        maintenanceJourneyName,
    });

    if (!maintenanceStatusApi || !maintenanceJourneyName) {
        throw new Error('Maintenance api config missing');
    }

    const response = await withMaintenanceTimeout(fetch(
        `${maintenanceStatusApi}?journeyName=${encodeURIComponent(maintenanceJourneyName)}`,
        {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'X-HSBC-Chnl-CountryCode': 'HK',
                'X-HSBC-Chnl-Group-Member': 'HBAP',
                'X-HSBC-Global-Channel-Id': 'WEB',
                'X-HSBC-Client-Id': 'dummy',
                'X-HSBC-Source-System-Id': '12648576',
                'X-HSBC-Request-Correlation-Id': uuidv4(),
                'Accept-Language': 'en_GB',
                'X-HSBC-Src-UserAgent': globalThis.navigator.userAgent,
            }
        }
    ));

    if (!response.ok) {
        throw new Error(`Fetch failed with status: ${response.status}`);
    }

    const payload = await response.json();
    console.log('[maintenance] api payload', payload);

    const normalizedPayload = normalizeMaintenanceApiPayload(payload);

    if (!normalizedPayload) {
        return normalizeMaintenanceWindow({});
    }

    const localConfig = await fetchMaintenanceLocalConfig().catch(() => ({}));
    const normalizedLocalConfig = normalizeMaintenanceWindow(localConfig);

    console.log('[maintenance] normalized payload', normalizedPayload);
    console.log('[maintenance] local fallback config', normalizedLocalConfig);

    return {
        ...normalizedPayload,
        title: mergeMaintenanceContent(normalizedPayload.title, normalizedLocalConfig.title),
        description: mergeMaintenanceContent(normalizedPayload.description, normalizedLocalConfig.description),
    };
};

export const fetchMaintenanceStatus = async () => {
    try {
        return await fetchMaintenanceApiStatus();
    } catch (error) {
        console.log('[maintenance] api fetch failed, using local config', error);
        const localConfig = normalizeMaintenanceWindow(await fetchMaintenanceLocalConfig());
        console.log('[maintenance] local config only', localConfig);
        return localConfig;
    }
};

export const resolveMaintenanceStatus = async (fallbackData = {}) => {
    try {
        return await fetchMaintenanceStatus();
    } catch (error) {
        console.log('[maintenance] resolve failed, using fallback', error);
        return normalizeMaintenanceWindow(fallbackData);
    }
};
